<template>
    <div class="quick-message-wrapper" v-click-outside="closeQuickMessages">
        <button
            type="button"
            class="btn btn-quick-message"
            style="border-radius: 5px !important;"
            @click="toggleQuickMessages()"
            :disabled="loadingQuickMessages"
            title="Inserir mensagem rápida"
        >
            <i v-if="loadingQuickMessages" class="fas fa-spinner fa-pulse"></i>
            <i v-else class="fas fa-bolt"></i>
            Mensagens Rápidas
            <i class="fas fa-chevron-up" v-if="quickMessageOpen"></i>
            <i class="fas fa-chevron-down" v-else></i>
        </button>

        <div class="quick-message-dropdown" v-if="quickMessageOpen">
            <div class="quick-message-search">
                <input
                    ref="quickMessageSearch"
                    type="text"
                    v-model="quickMessageSearch"
                    placeholder="Pesquisar mensagem..."
                    class="quick-message-search-input"
                >
            </div>
            <div class="quick-message-list">
                <div
                    v-if="filteredQuickMessages.length === 0"
                    class="quick-message-empty"
                >
                    <i class="fas fa-inbox"></i>
                    <span>Nenhuma mensagem encontrada.</span>
                </div>
                <div
                    v-for="qm in filteredQuickMessages"
                    :key="qm.id"
                    class="quick-message-item"
                    @click="selectQuickMessage(qm)"
                >
                    <div class="quick-message-item-name">
                        <i class="fas fa-bolt"></i>
                        {{ qm.name }}
                    </div>
                    <div class="quick-message-item-preview" v-html="qm.message"></div>
                </div>
            </div>
        </div>

        <div class="free-text-modal-overlay" v-if="freeTextModal.visible" @click.self="cancelFreeText()">
            <div class="free-text-modal">
                <div class="free-text-modal-header">
                    <i class="fas fa-pencil-alt"></i>
                    <span>Preencher campos de texto livre</span>
                </div>
                <div class="free-text-modal-body">
                    <p class="free-text-hint">
                        Esta mensagem contém campos de texto livre. Preencha-os antes de inserir:
                    </p>
                    <div
                        v-for="field in freeTextModal.fields"
                        :key="field.key"
                        class="free-text-field"
                    >
                        <label class="free-text-label">{{ field.label }}</label>
                        <input
                            type="text"
                            class="free-text-input"
                            v-model="field.value"
                            :placeholder="`Digite o conteúdo para ${field.label}...`"
                            @keydown.enter="confirmFreeText()"
                        >
                    </div>
                </div>
                <div class="free-text-modal-footer">
                    <button type="button" class="btn-free-text-cancel" @click="cancelFreeText()">
                        Cancelar
                    </button>
                    <button type="button" class="btn-free-text-confirm" @click="confirmFreeText()">
                        <i class="fas fa-check"></i> Inserir mensagem
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Botão "Mensagens Rápidas" para os editores de e-mail.
 * Resolve as variáveis do sistema ({{NomeCliente}}, {{protocolo}}, ...) com os dados da solicitação
 * e os campos de texto livre ({{texto1}}, ...) via modal, emitindo `insert` com o HTML final.
 */
export default {
    name: "QuickMessagePicker",
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutsideHandler = (event) => {
                    if (!el.contains(event.target)) {
                        binding.value();
                    }
                };
                document.addEventListener('click', el._clickOutsideHandler);
            },
            unbind(el) {
                document.removeEventListener('click', el._clickOutsideHandler);
            }
        }
    },
    props: ["emailGroups", "solicitationData"],
    data() {
        return {
            quickMessages: [],
            quickMessageOpen: false,
            quickMessageSearch: "",
            loadingQuickMessages: false,
            freeTextModal: {
                visible: false,
                fields: [],
                pendingMessage: '',
                resolve: null,
            },
        }
    },
    computed: {
        filteredQuickMessages() {
            if (!this.quickMessageSearch) return this.quickMessages;
            const term = this.quickMessageSearch.toLowerCase();
            return this.quickMessages.filter(qm =>
                qm.name.toLowerCase().includes(term)
            );
        }
    },
    watch: {
        quickMessageOpen(val) {
            if (val) {
                this.$nextTick(() => {
                    if (this.$refs.quickMessageSearch) {
                        this.$refs.quickMessageSearch.focus();
                    }
                });
            } else {
                this.quickMessageSearch = "";
            }
        }
    },
    methods: {
        async toggleQuickMessages() {
            if (this.quickMessageOpen) {
                this.closeQuickMessages();
                return;
            }

            await this.fetchQuickMessages();
            this.quickMessageOpen = true;
        },
        closeQuickMessages() {
            this.quickMessageOpen = false;
            this.quickMessageSearch = "";
        },
        async fetchQuickMessages() {
            this.loadingQuickMessages = true;
            try {
                const groupIds = (this.emailGroups || []).map(g => g.id).filter(Boolean);

                let response;
                if (groupIds.length > 0) {
                    response = await axios.get('/solicitacoes/quick-messages/by-groups', {
                        params: { groups: groupIds }
                    });
                } else {
                    response = await axios.get('/solicitacoes/quick-messages');
                }

                this.quickMessages = response.data;
            } catch (error) {
                console.error('Erro ao carregar mensagens rápidas:', error);
                this.quickMessages = [];
            } finally {
                this.loadingQuickMessages = false;
            }
        },
        resolveSystemVariables(message) {
            const data = this.solicitationData || {};

            const map = {
                NomeCliente: data.NomeCliente,
                sector:      data.sector,
                Base:        data.Base,
                nomePonto:   data.nomePonto,
                codigoPonto: data.codigoPonto,
                cep:         data.cep,
                endereco:    data.endereco,
                status:      data.status,
                chamado:     data.chamado,
                protocolo:   data.protocolo,
                cnpj:        data.cnpj,
                updated_by:  data.updated_by,
            };

            return message.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
                if (/^texto\d+$/.test(key)) return match;

                const value = map[key];
                return (value !== undefined && value !== null && value !== '')
                    ? value
                    : '---';
            });
        },
        async resolveFreeTextVariables(message) {
            const freeTextMatches = [...message.matchAll(/\{\{(texto\d+)\}\}/g)];
            if (!freeTextMatches.length) return message;

            const seen = new Set();
            const fields = [];
            for (const m of freeTextMatches) {
                const key = m[1];
                if (!seen.has(key)) {
                    seen.add(key);
                    const num = key.replace('texto', '');
                    fields.push({ key, label: `Texto ${num}`, value: '' });
                }
            }

            const filled = await new Promise((resolve) => {
                this.freeTextModal.fields         = fields;
                this.freeTextModal.pendingMessage = message;
                this.freeTextModal.resolve        = resolve;
                this.freeTextModal.visible        = true;
            });

            if (!filled) return null; // usuário cancelou

            let result = message;
            for (const field of this.freeTextModal.fields) {
                const regex = new RegExp(`\\{\\{${field.key}\\}\\}`, 'g');
                result = result.replace(regex, field.value || '---');
            }
            return result;
        },
        confirmFreeText() {
            this.freeTextModal.visible = false;
            if (this.freeTextModal.resolve) {
                this.freeTextModal.resolve(true);
                this.freeTextModal.resolve = null;
            }
        },
        cancelFreeText() {
            this.freeTextModal.visible = false;
            if (this.freeTextModal.resolve) {
                this.freeTextModal.resolve(false);
                this.freeTextModal.resolve = null;
            }
            this.freeTextModal.fields = [];
        },
        async selectQuickMessage(qm) {
            this.closeQuickMessages();

            let resolved = this.resolveSystemVariables(qm.message);

            resolved = await this.resolveFreeTextVariables(resolved);
            if (resolved === null) return; // usuário cancelou

            const tmp = document.createElement('div');
            tmp.innerHTML = resolved;
            tmp.querySelectorAll('.qm-variable-tag').forEach(span => {
                span.replaceWith(document.createTextNode(span.dataset.variable || span.textContent));
            });

            this.$emit('insert', tmp.innerHTML);
        },
    }
}
</script>

<style scoped>
.quick-message-wrapper {
    position: relative;
}

.btn-quick-message {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #f0a500, #f5b800);
    color: #fff;
    border: none;
    border-radius: 5px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(240, 165, 0, 0.3);
}

.btn-quick-message:hover:not(:disabled) {
    background: linear-gradient(135deg, #d4920a, #f0a500);
    box-shadow: 0 3px 10px rgba(240, 165, 0, 0.4);
}

.btn-quick-message:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.quick-message-dropdown {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 0;
    width: 340px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    overflow: hidden;
    animation: dropUp 0.15s ease;
}

@keyframes dropUp {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
}

.quick-message-search {
    padding: 10px;
    border-bottom: 1px solid #eee;
    background: #f8f9fa;
}

.quick-message-search-input {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 12px;
    outline: none;
}

.quick-message-search-input:focus {
    border-color: #f0a500;
    box-shadow: 0 0 3px rgba(240, 165, 0, 0.25);
}

.quick-message-list {
    max-height: 260px;
    overflow-y: auto;
}

.quick-message-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 24px;
    color: #aaa;
    font-size: 12px;
}

.quick-message-empty i {
    font-size: 22px;
}

.quick-message-item {
    padding: 10px 14px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.15s;
}

.quick-message-item:last-child {
    border-bottom: none;
}

.quick-message-item:hover {
    background: #fff8e6;
}

.quick-message-item-name {
    font-size: 12px;
    font-weight: 700;
    color: #333;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
}

.quick-message-item-name i {
    color: #f0a500;
    font-size: 11px;
}

.quick-message-item-preview {
    font-size: 11px;
    color: #777;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.4;
}

/* ---- Free Text Modal ---- */
.free-text-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(2px);
}

.free-text-modal {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.22);
    width: 480px;
    max-width: 95vw;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modalIn 0.18s ease;
}

@keyframes modalIn {
    from { opacity: 0; transform: scale(0.96); }
    to   { opacity: 1; transform: scale(1); }
}

.free-text-modal-header {
    background: linear-gradient(135deg, #f0a500, #f5b800);
    color: #333;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
}

.free-text-modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.free-text-hint {
    font-size: 12px;
    color: #666;
    margin: 0;
}

.free-text-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.free-text-label {
    font-size: 12px;
    font-weight: 700;
    color: #444;
}

.free-text-input {
    padding: 7px 10px;
    border: 1px solid #ced4da;
    border-radius: 5px;
    font-size: 13px;
    outline: none;
    transition: border-color 0.15s;
}

.free-text-input:focus {
    border-color: #f0a500;
    box-shadow: 0 0 3px rgba(240, 165, 0, 0.25);
}

.free-text-modal-footer {
    padding: 14px 20px;
    border-top: 1px solid #eee;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
    background: #fafafa;
}

.btn-free-text-cancel {
    background: none;
    border: 1px solid #ccc;
    color: #666;
    padding: 7px 16px;
    border-radius: 5px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-free-text-cancel:hover { background: #f0f0f0; border-color: #aaa; }

.btn-free-text-confirm {
    background: linear-gradient(135deg, #f0a500, #f5b800);
    border: none;
    color: #333;
    padding: 7px 18px;
    border-radius: 5px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
}

.btn-free-text-confirm:hover {
    background: linear-gradient(135deg, #d4920a, #f0a500);
    box-shadow: 0 3px 10px rgba(240, 165, 0, 0.35);
}
</style>
